import { Injectable, computed, inject } from '@angular/core';
import { AppService } from '../../core/app.service';
import { KeycloakPasswordAuthService } from '../../core/auth/keycloak-password-auth.service';
import { IssuePerson, issueMe } from './issues.types';

/**
 * The "current reporter" identity for the issue tracker. Prefers the REAL
 * signed-in Keycloak profile (name / username / sub); falls back to the
 * role-based mock person only when there's no active session (dev / no-auth).
 *
 * <p>Shared by the capture modal (issue-reporter), My Issues and the detail
 * thread so they all agree on who "me" is — otherwise the reporter shown in the
 * modal and the `reporterId` filed on the issue could disagree.
 */
@Injectable({ providedIn: 'root' })
export class IssueIdentityService {
  private readonly app = inject(AppService);
  private readonly auth = inject(KeycloakPasswordAuthService, { optional: true });

  readonly me = computed<IssuePerson>(() => {
    const fallback = issueMe(this.app.role() === 'admin' ? 'admin' : 'agent');
    const p = this.auth?.profile();
    if (!p) return fallback;
    const name =
      p.name?.trim() ||
      [p.givenName, p.familyName].filter(Boolean).join(' ').trim() ||
      p.preferredUsername ||
      fallback.name;
    const initials = p.initials?.trim() || fallback.initials;
    // Keep colour / role / tech from the role-based mock; override identity.
    return { ...fallback, id: p.sub || fallback.id, name, initials };
  });
}
